"use client";

import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, X } from "lucide-react";

interface YearPickerInputProps {
  value?: string | number;
  onChange: (year: string) => void;
  placeholder?: string;
  className?: string;
  minYear?: number;
  maxYear?: number;
  clearable?: boolean;
  disabled?: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();

export function YearPickerInput({
  value,
  onChange,
  placeholder = "Select year",
  className,
  minYear = 1920,
  maxYear = CURRENT_YEAR + 1,
  clearable = true,
  disabled = false,
}: YearPickerInputProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const displayValue = value ? String(value) : "";
  const selectedYear = displayValue ? parseInt(displayValue, 10) : null;

  // Build years array descending (newest first)
  const years: number[] = [];
  for (let y = maxYear; y >= minYear; y--) {
    years.push(y);
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Scroll selected year into view when opening
  useEffect(() => {
    if (!open || !selectedYear || !gridRef.current) return;
    const el = gridRef.current.querySelector<HTMLButtonElement>(
      `[data-year="${selectedYear}"]`
    );
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [open, selectedYear]);

  const handleSelect = (year: number) => {
    onChange(String(year));
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border-2 border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "hover:border-primary/60 transition-colors",
          !displayValue && "text-muted-foreground"
        )}
      >
        <span>{displayValue || placeholder}</span>
        <span className="flex items-center gap-1 ml-2 flex-shrink-0">
          {clearable && displayValue && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => e.key === "Enter" && handleClear(e as unknown as React.MouseEvent)}
              className="rounded-full hover:bg-muted p-0.5 transition-colors"
              aria-label="Clear year"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </span>
          )}
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={cn(
            "absolute z-50 mt-1 w-full min-w-[160px] rounded-md border border-border bg-popover shadow-lg",
            "overflow-hidden"
          )}
          style={{ maxHeight: "220px" }}
        >
          <div ref={gridRef} className="overflow-y-auto" style={{ maxHeight: "220px" }}>
            <div className="grid grid-cols-4 gap-0.5 p-1.5">
              {years.map((year) => (
                <button
                  key={year}
                  type="button"
                  data-year={year}
                  onClick={() => handleSelect(year)}
                  className={cn(
                    "rounded px-1 py-1.5 text-xs font-medium transition-colors",
                    year === selectedYear
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-foreground"
                  )}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default YearPickerInput;
